import {
    BranchStateService,
    CustomCodeUtilService,
    LanguagesService,
    ProjectInitializerService,
    ProjectService,
} from '@alpha-core/platform-ui';
import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable()
export class ProjectResolverService {
    private title = inject(Title);
    private projectService = inject(ProjectService);
    private languageService = inject(LanguagesService);
    private customCodeUtilService = inject(CustomCodeUtilService);
    private branchStateService = inject(BranchStateService);
    private router = inject(Router);

    async resolve(route: ActivatedRouteSnapshot): Promise<any> {
        const projectId = route.queryParams['projectId'];
        if (!projectId?.trim()) this.router.navigate(['404']);
        const project: any = await this.projectService.getAdminProject(projectId || '');
        // Branch validation is handled by branchValidationGuard before this resolver runs
        const branch = route.queryParams['branch'] || 'main';
        this.branchStateService.setBranch(branch);
        if (project && projectId) {
            this.projectService.setCurrentProject(project);
            this.title.setTitle('Admin - ' + project.name + ' - ' + project.projectId);
            if (project?.currentTag) {
                this.appendCssStyle(projectId, true);
                this.appendFontStyle(projectId, true);
                this.appendStyleVariable(projectId, true);
                await this.customCodeUtilService.appendCustomCode(this.getCustomCodeURL(projectId, true));
            } else {
                this.appendCssStyle(projectId);
                this.appendFontStyle(projectId);
                this.appendStyleVariable(projectId);
                await this.customCodeUtilService.appendCustomCode(this.getCustomCodeURL(projectId));
            }
        } else {
            // this.projectService.clearProject();
        }
        return projectId;
    }

    async fetchLanguages() {
        return await lastValueFrom(this.languageService.fetchLanguages());
    }

    private getCustomCodeURL(projectId: string, getCurrentTag: boolean = false) {
        return `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/case-config-server/${projectId}/custom-codes?getCurrentTag=${getCurrentTag}`;
    }

    appendCssStyle(projectId: string, getCurrentTag: boolean = false) {
        const branch = this.branchStateService.getBranch();
        this.createLink(
            `/ui/css/stylesheet/project/${projectId}?getCurrentTag=${getCurrentTag}&branch=${encodeURIComponent(branch)}`,
        );
    }

    appendFontStyle(projectId: string, getCurrentTag: boolean = false) {
        const branch = this.branchStateService.getBranch();
        this.createLink(
            `/ui/font-variables/fetch/project/${projectId}?getCurrentTag=${getCurrentTag}&branch=${encodeURIComponent(branch)}`,
        );
    }

    appendStyleVariable(projectId: string, getCurrentTag: boolean = false) {
        const branch = this.branchStateService.getBranch();
        this.createLink(
            `/ui/css/variables/project/${projectId}?getCurrentTag=${getCurrentTag}&branch=${encodeURIComponent(branch)}`,
        );
    }

    private createLink(url: string) {
        const link = document.createElement('link') as HTMLLinkElement;
        link.setAttribute('href', environment.ALPHA_UI_ADMIN_SERVICE_URL + '/case-config-server' + url);
        link.setAttribute('type', 'text/css');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('alphaGlobalStyle', '');
        document.head.appendChild(link);
    }

    setProjectId(projectId: string | null) {
        if (projectId) {
            window.alpha['project']['projectId'] = projectId;
        }
    }
}

@Injectable({ providedIn: 'root' })
export class ProjectWithInitResolver {
    private projectResolver = inject(ProjectResolverService);
    private projectInitService = inject(ProjectInitializerService);
    async resolve(route: ActivatedRouteSnapshot): Promise<any> {
        const projectId = await this.projectResolver.resolve(route);
        await this.projectInitService.init(
            projectId,
            false,
            `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/moduleservice/bundles`,
            `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/case-config-server`,
        );
        this.projectResolver.setProjectId(projectId);
        await this.projectResolver.fetchLanguages();
    }
}
